import React, { useState } from "react";
import { FieldError, UseFormRegister } from "react-hook-form";
import { Check, ChevronsUpDown, LucideIcon } from "lucide-react";
import FormField from "@/components/atoms/FormField";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
interface Option {
  value: string;
  icon?: LucideIcon;
  description?: string;
}
interface CommandSelectProps {
  label: string;
  options: readonly Option[] | Option[];
  value: string;
  onChange: (value: string) => void;
  register?: UseFormRegister<any>;
  customFieldName?: string;
  error?: FieldError;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  customFieldPlaceholder?: string;
}
const CommandSelect: React.FC<CommandSelectProps> = ({
  label,
  options,
  value,
  onChange,
  register,
  customFieldName,
  error,
  placeholder = "Select an option...",
  searchPlaceholder = "Search options...",
  emptyMessage = "No option found.",
  customFieldPlaceholder = "Enter custom value..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(option => option.value === value);
  return <div className="space-y-3" data-sentry-component="CommandSelect" data-sentry-source-file="index.tsx">
            <label className="block text-sm font-medium text-slate-200">
                {label}
            </label>
            <Dialog open={isOpen} onOpenChange={setIsOpen} data-sentry-element="Dialog" data-sentry-source-file="index.tsx">
                <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="index.tsx">
                    <Button variant="outline" role="combobox" aria-expanded={isOpen} className={cn("w-full justify-between !bg-slate-950 border-slate-700 text-slate-200 hover:!bg-background", !value && "text-slate-400")} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {selectedOption ? selectedOption.value : placeholder}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" data-sentry-element="ChevronsUpDown" data-sentry-source-file="index.tsx" />
                    </Button>
                </DialogTrigger>
                <DialogContent className="p-0 bg-slate-800/95 backdrop-blur-sm border-slate-700 sm:max-w-[425px] gap-0" data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
                    <Command className="bg-transparent" data-sentry-element="Command" data-sentry-source-file="index.tsx">
                        <CommandInput placeholder={searchPlaceholder} className="border-none focus:ring-0 bg-transparent" data-sentry-element="CommandInput" data-sentry-source-file="index.tsx" />
                        <CommandEmpty className="py-6 text-center text-sm text-slate-400" data-sentry-element="CommandEmpty" data-sentry-source-file="index.tsx">
                            {emptyMessage}
                        </CommandEmpty>
                        <div className="max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
                            <CommandList className="max-h-[90vh] sm:max-h-[60vh]" data-sentry-element="CommandList" data-sentry-source-file="index.tsx">
                                <CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="index.tsx">
                                    {options.map(({
                  value: optionValue,
                  icon: Icon,
                  description
                }) => <CommandItem key={optionValue} value={optionValue} onSelect={currentValue => {
                  onChange(currentValue);
                  setIsOpen(false);
                }} className="flex items-start gap-3 p-3 cursor-pointer aria-selected:!bg-sky-400/20 hover:!bg-sky-400/10">
                                                <div className="flex items-center gap-3 flex-1">
                                                    {Icon && <Icon className={cn("h-5 w-5", value === optionValue ? "text-sky-400" : "text-slate-400")} />}
                                                    <div className="flex flex-col gap-1">
                                                        <span className={cn("font-medium", value === optionValue ? "text-sky-400" : "text-slate-200")}>
                                                            {optionValue}
                                                        </span>
                                                        {description && <span className="text-xs text-slate-400">
                                                                {description}
                                                            </span>}
                                                    </div>
                                                </div>
                                                {value === optionValue && <Check className="h-4 w-4 text-sky-400 shrink-0" />}
                                            </CommandItem>)}
                                </CommandGroup>
                            </CommandList>
                        </div>
                    </Command>
                </DialogContent>
            </Dialog>

            {value === "Other" && customFieldName && register && <FormField label={`Custom ${label}`} register={register(customFieldName, {
      required: `Custom ${label.toLowerCase()} is required when 'Other' is selected`
    })} error={error} placeholder={customFieldPlaceholder} />}
        </div>;
};
export default CommandSelect;